import { gql } from "generated-graphql";
import {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";

import type { ScoutConversationPrivateDocumentRow } from "./useScoutConversationPrivateDocuments";

import { SCOUT_NATIVE_MAX_PDF_BYTES } from "../scoutAttachmentLimits";
import { isScoutOptimisticConversationId } from "./scoutConversationIds";

/** Statuses where extraction work is in flight. */
export const SCOUT_EXTRACTION_IN_PROGRESS_STATUSES = new Set([
  ScoutPrivateDocumentExtractionStatus.PreviewRunning,
  ScoutPrivateDocumentExtractionStatus.Pending,
  ScoutPrivateDocumentExtractionStatus.Running,
]);

export const GET_SCOUT_CONVERSATION_PRIVATE_DOCUMENTS = gql(`
  query GetScoutConversationPrivateDocuments($tenantId: ID!, $conversationId: ID!) {
    getScoutConversationPrivateDocuments(tenantId: $tenantId, conversationId: $conversationId) {
      id
      filename
      fileExtension
      mediaType
      sizeBytes
      pageCount
      ingestionMode
      extractionStatus
      previewSummary
      signedDownloadUrl
      thumbnailSignedUrl
      extractionProgress {
        pagesCompleted
        pagesTotal
      }
    }
  }
`);

const PERSISTED_PRIVATE_DOCUMENT_ID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isTextExtractionAttachment(input: {
  ingestionMode?: string;
  sizeBytes?: number;
}): boolean {
  if (
    input.ingestionMode === ScoutPrivateDocumentIngestionMode.LargeFileExtraction ||
    input.ingestionMode === "LARGE_FILE_EXTRACTION"
  ) {
    return true;
  }

  return (input.sizeBytes ?? 0) > SCOUT_NATIVE_MAX_PDF_BYTES;
}

/** @deprecated Use isTextExtractionAttachment */
export const isLargePdfExtractionAttachment = isTextExtractionAttachment;

export function isTextExtractionDocument(document?: ScoutConversationPrivateDocumentRow): boolean {
  return document?.ingestionMode === ScoutPrivateDocumentIngestionMode.LargeFileExtraction;
}

/** @deprecated Use isTextExtractionDocument */
export const isLargePdfExtractionDocument = isTextExtractionDocument;

/** Server attachment order is oldest-first; the active document is the most recent match. */
export function pickMostRecentMatchingPrivateDocument(
  documents: ScoutConversationPrivateDocumentRow[],
  matches: (document: ScoutConversationPrivateDocumentRow) => boolean
): ScoutConversationPrivateDocumentRow | undefined {
  for (let index = documents.length - 1; index >= 0; index -= 1) {
    const document = documents[index];
    if (matches(document)) {
      return document;
    }
  }

  return undefined;
}

/** Most recently attached LARGE_FILE_EXTRACTION document in the conversation. */
export function pickMostRecentTextExtractionDocument(
  documents: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow | undefined {
  return pickMostRecentMatchingPrivateDocument(documents, isTextExtractionDocument);
}

/** @deprecated Use pickMostRecentTextExtractionDocument */
export const pickMostRecentLargePdfDocument = pickMostRecentTextExtractionDocument;

export function isPersistedPrivateDocumentId(id: string): boolean {
  return PERSISTED_PRIVATE_DOCUMENT_ID_REGEX.test(id);
}

export function pickActiveLargeDocumentExtraction(
  documents: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow | undefined {
  const mostRecent = pickMostRecentTextExtractionDocument(documents);
  if (!mostRecent || !SCOUT_EXTRACTION_IN_PROGRESS_STATUSES.has(mostRecent.extractionStatus)) {
    return undefined;
  }

  return mostRecent;
}

/** Statuses where async work is still in flight for a large-document row. */
export const SCOUT_LARGE_DOCUMENT_PENDING_STATUSES = new Set([
  ScoutPrivateDocumentExtractionStatus.NotAttempted,
  ScoutPrivateDocumentExtractionStatus.PreviewRunning,
  ScoutPrivateDocumentExtractionStatus.Pending,
  ScoutPrivateDocumentExtractionStatus.Running,
]);

/** Statuses eligible for the footer opt-in while preview / confirmation is pending. */
export const SCOUT_LARGE_DOCUMENT_OPT_IN_STATUSES = new Set([
  ScoutPrivateDocumentExtractionStatus.NotAttempted,
  ScoutPrivateDocumentExtractionStatus.PreviewRunning,
  ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation,
]);

export function shouldRetainPendingLargeDocumentRow(
  document: ScoutConversationPrivateDocumentRow
): boolean {
  return (
    isTextExtractionDocument(document) &&
    (SCOUT_LARGE_DOCUMENT_PENDING_STATUSES.has(document.extractionStatus) ||
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation)
  );
}

/** Whether private-document React state should be cleared when the conversation id changes. */
export function shouldResetPrivateDocumentsOnConversationChange(
  previousConversationId: string | undefined,
  nextConversationId: string | undefined
): boolean {
  if (previousConversationId === nextConversationId) {
    return false;
  }

  if (!nextConversationId) {
    return Boolean(previousConversationId);
  }

  if (!previousConversationId) {
    return !isScoutOptimisticConversationId(nextConversationId);
  }

  if (isScoutOptimisticConversationId(previousConversationId)) {
    return false;
  }

  return true;
}

export function mergeScoutPrivateDocumentRows(
  previous: ScoutConversationPrivateDocumentRow[],
  fetched: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow[] {
  if (fetched.length === 0) {
    return previous.filter(shouldRetainPendingLargeDocumentRow);
  }

  const byId = new Map(previous.map((row) => [row.id, row]));
  for (const row of fetched) {
    const existing = byId.get(row.id);
    byId.set(
      row.id,
      existing
        ? {
            ...existing,
            ...row,
            filename: row.filename || existing.filename,
          }
        : row
    );
  }

  const mergedFromFetched = fetched
    .map((row) => byId.get(row.id))
    .filter((row): row is ScoutConversationPrivateDocumentRow => row !== undefined);

  const fetchedIds = new Set(fetched.map((row) => row.id));
  const retainedPending = previous.filter(
    (row) =>
      !fetchedIds.has(row.id) &&
      shouldRetainPendingLargeDocumentRow(row) &&
      (isPersistedPrivateDocumentId(row.id) || fetched.length === 0)
  );

  return [...retainedPending, ...mergedFromFetched];
}

/** Update known rows in place without treating a partial list as the full attachment query. */
export function upsertScoutPrivateDocumentRows(
  previous: ScoutConversationPrivateDocumentRow[],
  updates: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow[] {
  if (updates.length === 0) {
    return previous;
  }

  const next = [...previous];
  for (const row of updates) {
    const existingIndex = next.findIndex((existing) => existing.id === row.id);
    if (existingIndex >= 0) {
      const existing = next[existingIndex];
      next[existingIndex] = {
        ...existing,
        ...row,
        filename: row.filename || existing.filename,
      };
    } else {
      next.push(row);
    }
  }

  return next;
}

/** Append newly attached docs as most recent without reordering existing rows. */
export function appendScoutPrivateDocumentRows(
  previous: ScoutConversationPrivateDocumentRow[],
  appended: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow[] {
  if (appended.length === 0) {
    return previous;
  }

  const next = [...previous];
  for (const row of appended) {
    const existingIndex = next.findIndex((existing) => existing.id === row.id);
    if (existingIndex >= 0) {
      const existing = next[existingIndex];
      next[existingIndex] = {
        ...existing,
        ...row,
        filename: row.filename || existing.filename,
      };
    } else {
      next.push(row);
    }
  }

  return next;
}

/** Statuses that should keep the private-document SSE stream connected. */
export const SCOUT_PRIVATE_DOCUMENT_STREAM_WATCH_STATUSES = new Set([
  ...SCOUT_EXTRACTION_IN_PROGRESS_STATUSES,
  ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation,
  ScoutPrivateDocumentExtractionStatus.NotAttempted,
  ScoutPrivateDocumentExtractionStatus.PreviewRunning,
]);

export function conversationNeedsPrivateDocumentStream(
  documents: ScoutConversationPrivateDocumentRow[]
): boolean {
  return documents.some(
    (document) =>
      isPersistedPrivateDocumentId(document.id) &&
      isTextExtractionDocument(document) &&
      SCOUT_PRIVATE_DOCUMENT_STREAM_WATCH_STATUSES.has(document.extractionStatus)
  );
}

export function pickLargeDocumentOptInCandidates(
  documents: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow[] {
  return documents.filter(
    (document) =>
      isTextExtractionDocument(document) &&
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation
  );
}

/** @deprecated Use pickLargeDocumentOptInCandidates instead */
export function pickLargeDocumentOptInCandidate(
  documents: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow | undefined {
  return pickLargeDocumentOptInCandidates(documents).at(-1);
}

const TRACKABLE_LARGE_DOCUMENT_JOB_STATUSES = new Set([
  ScoutPrivateDocumentExtractionStatus.PreviewRunning,
  ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation,
  ScoutPrivateDocumentExtractionStatus.Pending,
  ScoutPrivateDocumentExtractionStatus.Running,
  ScoutPrivateDocumentExtractionStatus.Succeeded,
  ScoutPrivateDocumentExtractionStatus.Failed,
]);

export function pickLargeDocumentJob(
  documents: ScoutConversationPrivateDocumentRow[]
): ScoutConversationPrivateDocumentRow | undefined {
  const mostRecent = pickMostRecentTextExtractionDocument(documents);
  if (!mostRecent || !TRACKABLE_LARGE_DOCUMENT_JOB_STATUSES.has(mostRecent.extractionStatus)) {
    return undefined;
  }

  return mostRecent;
}
